/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Grid from "@mui/material/Grid";
import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import { useState, useMemo, useEffect } from "react";
import axios from "axios";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDAvatar from "components/MDAvatar";

// NewProduct page components
import FormField from "layouts/ecommerce/products/new-product/components/FormField";

// Import variabel API
import { API_HOHOLOK, HOHOLOK_UPLOADS } from "utils";

// State untuk opsi penerima
const defaultOptions = [];

function PelaksanaProyek({ values = {}, onChange }) {
  const [penerimaOptions, setPenerimaOptions] = useState(defaultOptions);
  // Tambahan state untuk perusahaan
  const [kontraktorOptions, setKontraktorOptions] = useState([]);
  const [konsultanPengawasOptions, setKonsultanPengawasOptions] = useState([]);
  const [konsultanPerencanaOptions, setKonsultanPerencanaOptions] = useState(
    []
  );
  const [akunKontraktorUserOptions, setAkunKontraktorUserOptions] = useState(
    []
  );
  const [akunPengawasUserOptions, setAkunPengawasUserOptions] = useState([]);

  // Fetch data perusahaan
  useEffect(() => {
    axios
      .get(`${API_HOHOLOK}perusahaan`)
      .then((res) => {
        const data = res.data || [];

        setKontraktorOptions(
          data
            .filter((item) => item.jenis_perusahaan === "Kontraktor")
            .map((item) => ({
              label: item.nama_perusahaan,
              value: item.id_perusahaan,
            }))
        );
        setKonsultanPengawasOptions(
          data
            .filter((item) => item.jenis_perusahaan === "Konsultan Pengawas")
            .map((item) => ({
              label: item.nama_perusahaan,
              value: item.id_perusahaan,
            }))
        );
        setKonsultanPerencanaOptions(
          data
            .filter((item) => item.jenis_perusahaan === "Konsultan Perencana")
            .map((item) => ({
              label: item.nama_perusahaan,
              value: item.id_perusahaan,
            }))
        );
      })
      .catch(() => {
        setKontraktorOptions([]);
        setKonsultanPengawasOptions([]);
        setKonsultanPerencanaOptions([]);
      });
  }, []);

  // Trigger fetch users setiap kontraktor_pelaksana berubah
  useEffect(() => {
    if (values.kontraktor_pelaksana) {
      axios
        .get(`${API_HOHOLOK}users/perusahaan/${values.kontraktor_pelaksana}`)
        .then((res) => {
          const options = (res.data || []).map((data) => ({
            label: data.nama_user ? data.nama_user.split(" ")[0] : "-",
            value: data.id_user,
            avatar: data.profil
              ? `${HOHOLOK_UPLOADS}${data.profil}`
              : undefined,
          }));
          setAkunKontraktorUserOptions(options);
        })
        .catch((err) => {
          setAkunKontraktorUserOptions([]);
        });
    }
  }, [values.kontraktor_pelaksana]);

  // Trigger fetch users setiap kontraktor_pelaksana berubah
  useEffect(() => {
    if (values.konsultan_pengawas) {
      axios
        .get(`${API_HOHOLOK}users/perusahaan/${values.konsultan_pengawas}`)
        .then((res) => {
          const options = (res.data || []).map((data) => ({
            label: data.nama_user ? data.nama_user.split(" ")[0] : "-",
            value: data.id_user,
            avatar: data.profil
              ? `${HOHOLOK_UPLOADS}${data.profil}`
              : undefined,
          }));
          setAkunPengawasUserOptions(options);
        })
        .catch((err) => {
          setAkunPengawasUserOptions([]);
        });
    }
  }, [values.konsultan_pengawas]);

  return (
    <MDBox>
      <MDTypography variant="h5">Pelaksana Proyek</MDTypography>
      <MDBox mt={1}>
        <Grid container spacing={3}></Grid>
        <MDBox mt={2}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Autocomplete
                options={konsultanPerencanaOptions}
                value={
                  konsultanPerencanaOptions.find(
                    (opt) => opt.value === values.konsultan_perencana
                  ) || null
                }
                onChange={(event, newValue) => {
                  onChange({
                    konsultan_perencana: newValue ? newValue.value : null,
                  });
                }}
                renderInput={(params) => (
                  <MDInput
                    {...params}
                    label="Konsultan Perencana"
                    variant="standard"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </MDBox>
        <MDBox mt={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={kontraktorOptions}
                value={
                  kontraktorOptions.find(
                    (opt) => opt.value === values.kontraktor_pelaksana
                  ) || null
                }
                onChange={(event, newValue) => {
                  onChange({
                    kontraktor_pelaksana: newValue ? newValue.value : null,
                  });
                }}
                renderInput={(params) => (
                  <MDInput
                    {...params}
                    label="Kontraktor Pelaksana"
                    variant="standard"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={akunKontraktorUserOptions}
                value={
                  akunKontraktorUserOptions.find(
                    (opt) => opt.value === values.akunKontraktor
                  ) || null
                }
                onChange={(event, newValue) => {
                  onChange({
                    akunKontraktor: newValue ? newValue.value : null,
                  });
                }}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value}
                renderInput={(params) => (
                  <MDInput
                    {...params}
                    variant="standard"
                    label="Akun Kontraktor"
                  />
                )}
              />
            </Grid>
          </Grid>
        </MDBox>
        <MDBox mt={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={konsultanPengawasOptions}
                value={
                  konsultanPengawasOptions.find(
                    (opt) => opt.value === values.konsultan_pengawas
                  ) || null
                }
                onChange={(event, newValue) => {
                  onChange({
                    konsultan_pengawas: newValue ? newValue.value : null,
                  });
                }}
                renderInput={(params) => (
                  <MDInput
                    {...params}
                    label="Konsultan Pengawas"
                    variant="standard"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={akunPengawasUserOptions}
                value={
                  akunPengawasUserOptions.find(
                    (opt) => opt.value === values.akunPengawas
                  ) || null
                }
                onChange={(event, newValue) => {
                  onChange({ akunPengawas: newValue ? newValue.value : null });
                }}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value}
                renderInput={(params) => (
                  <MDInput
                    {...params}
                    variant="standard"
                    label="Akun Pengawas"
                  />
                )}
              />
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
    </MDBox>
  );
}

export default PelaksanaProyek;
